import cx from 'classnames'
import Downshift, { DownshiftState, StateChangeOptions } from 'downshift'
import isEmpty from 'lodash/isEmpty'
import matchSorter from 'match-sorter'
import * as React from 'react'

import type { ShowFromListViewModel } from '../../view-models/ShowFromListViewModel'
import { trackEvent } from '../../utils/gtag'
import scrollToElement from '../../utils/ScrollToElement'
import CtaButton from '../CtaButton/CtaButton'
import DropshiftDropdown from '../DropshiftDropdown/DropshiftDropdown'
import Heading from '../Heading/Heading'
import Paragraph from '../Paragraph/Paragraph'
import Rte from '../Rte/Rte'
import styles from './showChosenFromList-style.css'
import { LinkButton } from '@/components/Button/Button'

let id = 0

const genId = () => ++id

type Props = {
  heading?: string
  text?: string
  placeholder?: string
  items: ShowFromListViewModel[]
}

type State = {
  isOpen: boolean
  itemsToShow: ShowFromListViewModel[]
  selected: ShowFromListViewModel | null
  downshiftId: number | string
}

// Define a type for Downshift changes to make it more explicit
type DownshiftChangeOptions = StateChangeOptions<ShowFromListViewModel>
type DownshiftStateType = DownshiftState<ShowFromListViewModel>

class ShowChosenFromList extends React.Component<Props, State> {
  static displayName = 'ShowChosenFromList'
  static defaultProps = {}
  state: State = {
    isOpen: false,
    itemsToShow: [],
    selected: null,
    downshiftId: '',
  }

  componentDidMount() {
    if (this.props.items) {
      this.setState({
        itemsToShow: this.props.items,
        downshiftId: genId(),
      })
    }
  }

  handleStateChange = (
    changes: Partial<DownshiftChangeOptions>,
    downshiftState: DownshiftStateType,
  ) => {
    if (changes.hasOwnProperty('isOpen')) {
      this.setState(({ isOpen, itemsToShow }) => {
        isOpen =
          changes.type === Downshift.stateChangeTypes.mouseUp
            ? isOpen
            : changes.isOpen || false

        if (isOpen) {
          itemsToShow = this.getItemsToShow(downshiftState.inputValue || '')
        }

        return {
          isOpen,
          itemsToShow,
        }
      })
    }

    if (changes.hasOwnProperty('inputValue')) {
      // Explicitly cast the selectedItem to our expected type
      const selectedItem = changes.selectedItem as
        | ShowFromListViewModel
        | undefined

      this.setState(
        {
          itemsToShow: this.getItemsToShow(downshiftState.inputValue || ''),
          selected: selectedItem || null,
        },
        () => {
          if (this.state.selected) {
            // scroll up
            scrollToElement(`choose-mod-${this.state.downshiftId}`, 50)
            trackEvent({
              event: 'list-pick',
              eventLabel: this.state.selected.keypair.value,
            })
          }
        },
      )
    }
  }

  handleFocus = () => {
    this.setState({
      isOpen: true,
    })
  }

  handleOuterClick = () => {
    this.setState({
      isOpen: false,
    })
  }

  handleChange = (selectedItem: ShowFromListViewModel | null) => {
    this.setState({
      selected: selectedItem,
    })

    if (selectedItem) {
      scrollToElement(`choose-mod-xs-${this.state.downshiftId}`, 50)
      trackEvent({
        event: 'list-pick',
        eventLabel: selectedItem.keypair.value,
      })
    }
  }

  getItemsToShow = (value: string) => {
    return value
      ? matchSorter(this.props.items, value, {
          keys: ['keypair.value'],
        })
      : this.props.items
  }

  itemToString = (item: Record<string, any>) => {
    // Check if the item has the expected structure
    if (
      item &&
      'keypair' in item &&
      typeof item.keypair === 'object' &&
      item.keypair &&
      'value' in item.keypair
    ) {
      return item.keypair.value
    }
    return ''
  }

  render() {
    const { heading, text, placeholder, ...rest } = this.props
    const { isOpen, itemsToShow, selected } = this.state

    return (
      <div className={styles.showChosenFromList}>
        {heading && (
          <Heading level={4} tag={'h3'} className={styles.heading}>
            {heading}
          </Heading>
        )}
        {text && (
          <Paragraph
            marginBottom={'normal'}
            size={'small'}
            weight={'light'}
            tag={'p'}
            className={cx(styles.p, isOpen && styles.open)}
          >
            {text}
          </Paragraph>
        )}
        {placeholder && (
          <DropshiftDropdown
            onStateChange={this.handleStateChange}
            onFocus={this.handleFocus}
            onOuterClick={this.handleOuterClick}
            isOpen={isOpen}
            onChange={this.handleChange}
            {...rest}
            items={itemsToShow}
            itemToString={this.itemToString}
            placeholder={placeholder}
            id={`choose-mod-${this.state.downshiftId}`}
            mobileId={`choose-mod-xs-${this.state.downshiftId}`}
          />
        )}
        {selected && !isEmpty(selected) && (
          <div className={styles.content}>
            {selected.source && (
              <Rte className={styles.contentMarkup} source={selected.source} />
            )}
            {selected.cta && (
              <>
                <LinkButton
                  {...selected.cta}
                  variant={selected.cta.version}
                  className={styles.resultCta}
                >
                  {selected.cta.link?.label}
                </LinkButton>
              </>
            )}
          </div>
        )}
      </div>
    )
  }
}

export default ShowChosenFromList
